import React from "react";
import styled from "styled-components";
import { Form, Formik, FormikHelpers } from "formik";
import * as Sentry from "@sentry/react";
import TextLink from "../library/TextLink";
import SubmitButton from "../library/Form/SubmitButton";
import RadioField from "../library/Form/RadioField";
import RadioFieldGroup from "../library/Form/RadioFieldGroup";
import RecruiterUser, {
  BothJobTypes,
  RecruiterUserJobType,
} from "../../apiResources/RecruiterUser";
import { JobType } from "../../apiResources/Job";
import Skeleton from "../library/Skeleton";
import SettingLabel from "./SettingLabel";

const JobTypeSettingOption = (props: Props) => {
  const {
    user,
    isEditing = false,
    disabled = false,
    isLoading = false,
    onEdit,
    onDone,
    onUserChange,
  } = props;

  const save = async (
    values: FormValues,
    formik: FormikHelpers<FormValues>
  ) => {
    if (values.jobType === user.jobType) {
      onDone();
      return;
    }

    try {
      user.assignAttributes(values);
      const success = await user.save();
      if (success) {
        onUserChange?.(user);
        onDone();
      } else {
        formik.setErrors({
          jobType: user.errors.jobType.fullMessage,
        });
      }
    } catch (err) {
      Sentry.captureException(err);
    }
  };

  const edit = () => {
    if (!disabled) {
      onEdit();
    }
  };

  const jobType = user?.jobType ?? BothJobTypes;
  let displayValue = "Permanent and Contract";

  if (jobType === JobType.PERMANENT) {
    displayValue = "Permanent";
  } else if (jobType === JobType.CONTRACT) {
    displayValue = "Contract";
  }

  if (!isEditing) {
    return (
      <section>
        <Header>
          <SettingLabel>Job Type</SettingLabel>
          <TextLink disabled={disabled} onClick={edit}>
            Edit
          </TextLink>
        </Header>
        {isLoading && <Skeleton height="22" />}
        <div>{displayValue}</div>
      </section>
    );
  }

  const initialValues: FormValues = { jobType };

  return (
    <section>
      <Formik initialValues={initialValues} onSubmit={save}>
        <Form autoComplete="off">
          <Header>
            <SettingLabel>Job Type</SettingLabel>
            <TextLink onClick={onDone}>Cancel</TextLink>
          </Header>

          <RadioFieldGroup name="jobType" label="Job Type" hiddenLabel>
            <RadioField
              label="Permanent"
              name="jobType"
              value={JobType.PERMANENT}
            />
            <RadioField
              label="Contract"
              name="jobType"
              value={JobType.CONTRACT}
            />
            <RadioField
              label="Both - Permanent & Contract"
              name="jobType"
              value={BothJobTypes}
            />
          </RadioFieldGroup>

          <SubmitButton />
        </Form>
      </Formik>
    </section>
  );
};

type Props = {
  user: RecruiterUser;
  isEditing?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  onEdit: () => void;
  onDone: () => void;
  onUserChange?: (user: RecruiterUser) => void;
};

type FormValues = {
  jobType: RecruiterUserJobType;
};

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

export default JobTypeSettingOption;
